import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Icon, Spinner } from "@chakra-ui/react";
import { Inbox, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { statusMeta } from "@/lib/submissionMeta";
import { displayStatus } from "@/lib/urgent";
import { ScheduleFormButton } from "@/components/ScheduleFormButton";
import { ACK_META, ackStatus, signedCounts } from "@/components/AcknowledgeModal";
import { SubmissionRow, AckRow, AssignmentRow, fmt, fmtDue } from "@/components/submissions/SubmissionRows";
import { SubmissionFilters, ACK_TYPE, FORM_ASSIGN_TYPE } from "@/components/submissions/SubmissionFilters";
import { colors } from "@/theme/colors";

const ACK_STATUSES = ["assigned", "awaiting_signatures", "completed", "overdue", "superseded"];
const FA_STATUSES = ["assigned", "overdue"];
const SUB_STATUSES = ["pending", "resolved", "superseded"];

const ackParticipants = (a) => [...new Set([a.worker_name, ...(a.assignment_signoffs || []).map((s) => s.worker_name)].filter(Boolean))];
const signersText = (a) => (a.assignment_signoffs || [])
  .map((s) => `${s.worker_name}${s.status === "signed" ? ` (signed ${fmt(s.signed_at)})` : " (pending)"}`).join("; ");

export default function Submissions() {
  const navigate = useNavigate();
  const { user, activeCompanyId, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyF, setCompanyF] = useState("");
  const [f, setF] = useState({
    type: searchParams.get("type") || "",
    location: searchParams.get("location") || "",
    worker: "",
    status: searchParams.get("status") || "",
    source: searchParams.get("source") || "",
    contractor: "",
    from: "", to: "",
  });

  // Re-apply URL filters when they change while this page is already mounted
  // (e.g. the bell's "view all" tapped from this very dashboard).
  useEffect(() => {
    setF((prev) => ({
      ...prev,
      type: searchParams.get("type") || "",
      location: searchParams.get("location") || "",
      status: searchParams.get("status") || "",
      source: searchParams.get("source") || "",
    }));
  }, [searchParams]);

  const load = useCallback(async () => {
    if (!activeCompanyId && !isSuperAdmin) return;
    // Tenant isolation: every list query is pinned to the ACTING company —
    // RLS alone returns the union of all memberships, which must never render.
    // DELIBERATE exception: Super-Admins get the platform view (all companies,
    // clearly labelled, with a Company column) — see the isSuperAdmin branches.
    const subQ = supabase.from("submissions")
      .select("id,company_id,template_title,template_type,template_version,location_name,worker_name,status,flagged,flag_reasons,revision,created_at,completed_at,source,contractor_names,contractor_company")
      .order("created_at", { ascending: false }).limit(1000);
    const faQ = supabase.from("form_assignments").select("*, assignment_signoffs(*)")
      .order("created_at", { ascending: false }).limit(1000);
    const [subRes, faRes, coRes] = await safeAll([
      isSuperAdmin ? subQ : subQ.eq("company_id", activeCompanyId),
      isSuperAdmin ? faQ : faQ.eq("company_id", activeCompanyId),
      isSuperAdmin
        ? supabase.from("companies").select("id,name").order("name")
        : Promise.resolve({ data: [], error: null }),
    ]);
    if (!coRes.error) setCompanies(coRes.data || []);
    if (subRes.error || faRes.error) {
      // Offline: serve the last-known-good history instead of an empty state.
      const snap = cacheGet(user.id, "submissions") || {};
      setRows(snap.rows || []);
      setAssignments(snap.assignments || []);
    } else {
      setRows(subRes.data || []);
      setAssignments(faRes.data || []);
      cachePut(user.id, "submissions", {
        rows: (subRes.data || []).slice(0, 300),
        assignments: (faRes.data || []).slice(0, 300),
      });
    }
    setLoading(false);
  }, [user.id, activeCompanyId, isSuperAdmin]);
  useEffect(() => { load(); }, [load]);

  const acks = useMemo(() => assignments.filter((a) => a.kind === "resource"), [assignments]);
  // Open scheduled-form assignments: once the worker submits, the linked
  // submission row (tagged "Scheduled") becomes the SAME tracked item — no duplicate.
  const formAssigns = useMemo(
    () => assignments.filter((a) => a.kind === "form" && !a.submission_id && a.status !== "superseded" && a.status !== "completed"),
    [assignments]);
  const assignBySubmission = useMemo(
    () => Object.fromEntries(assignments.filter((a) => a.submission_id).map((a) => [a.submission_id, a])), [assignments]);

  const types = useMemo(() => [...new Set(rows.map((r) => r.template_type).filter(Boolean))].sort(), [rows]);
  const locations = useMemo(() => [...new Set([...rows.map((r) => r.location_name), ...assignments.map((a) => a.location_name)].filter(Boolean))].sort(), [rows, assignments]);
  const workers = useMemo(() => [...new Set([
    ...rows.map((r) => r.worker_name),
    ...assignments.flatMap((a) => ackParticipants(a)),
  ].filter(Boolean))].sort(), [rows, assignments]);
  const contractorNames = useMemo(
    () => [...new Set(rows.flatMap((r) => r.contractor_names || []))].sort(), [rows]);

  const dateOk = useCallback(
    (d) => (!f.from || new Date(d) >= new Date(`${f.from}T00:00:00`)) && (!f.to || new Date(d) <= new Date(`${f.to}T23:59:59`)),
    [f.from, f.to]
  );

  const filteredSubs = useMemo(() => rows.filter((r) => {
    const hasContractor = (r.contractor_names || []).length > 0;
    const fa = assignBySubmission[r.id];
    const signerNames = fa ? (fa.assignment_signoffs || []).map((s) => s.worker_name) : [];
    if (f.type === ACK_TYPE) return false;
    if (f.type && r.template_type !== f.type) return false;
    if (f.location && r.location_name !== f.location) return false;
    if (f.worker && r.worker_name !== f.worker && !signerNames.includes(f.worker)) return false;
    if (f.status) {
      if (f.status === "pending" || f.status === "logged") { if (displayStatus(r) !== f.status) return false; }
      else if (SUB_STATUSES.includes(f.status)) { if (r.status !== f.status) return false; }
      else if (f.status === "awaiting_signatures") { if (fa?.status !== "awaiting_signatures") return false; }
      else return false;
    }
    if (f.source === "employee" && r.source === "contractor") return false;
    if (f.source === "portal" && r.source !== "contractor") return false;
    if (f.source === "signoff" && !(r.source !== "contractor" && hasContractor)) return false;
    if (f.source === "any_contractor" && !(r.source === "contractor" || hasContractor)) return false;
    if (f.contractor && !(r.contractor_names || []).includes(f.contractor)) return false;
    return dateOk(r.created_at);
  }), [rows, f, assignBySubmission, dateOk]);

  const filteredAcks = useMemo(() => acks.filter((a) => {
    if (f.source || f.contractor) return false;
    if (f.type && f.type !== ACK_TYPE) return false;
    if (f.location && a.location_name !== f.location) return false;
    if (f.worker && !ackParticipants(a).includes(f.worker)) return false;
    if (f.status) {
      if (!ACK_STATUSES.includes(f.status)) return false;
      if (ackStatus(a) !== f.status) return false;
    }
    return dateOk(a.created_at);
  }), [acks, f, dateOk]);

  const filteredFAs = useMemo(() => formAssigns.filter((a) => {
    if (f.source || f.contractor) return false;
    if (f.type && f.type !== FORM_ASSIGN_TYPE) return false;
    if (f.location && a.location_name !== f.location) return false;
    if (f.worker && !ackParticipants(a).includes(f.worker)) return false;
    if (f.status) {
      if (!FA_STATUSES.includes(f.status)) return false;
      if (ackStatus(a) !== f.status) return false;
    }
    return dateOk(a.created_at);
  }), [formAssigns, f, dateOk]);

  const merged = useMemo(() => [
    ...filteredSubs.map((r) => ({ rowType: "sub", ts: r.created_at, r })),
    ...filteredAcks.map((a) => ({ rowType: "ack", ts: a.created_at, a })),
    ...filteredFAs.map((a) => ({ rowType: "fa", ts: a.created_at, a })),
  ].sort((x, y) => new Date(y.ts) - new Date(x.ts)), [filteredSubs, filteredAcks, filteredFAs]);

  const hasFilters = Object.values(f).some(Boolean);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const companyName = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);
  // Platform view (Super-Admin): optional single-company narrowing on top of merged.
  const visible = useMemo(() => companyF
    ? merged.filter((m) => (m.r?.company_id || m.a?.company_id) === companyF)
    : merged, [merged, companyF]);

  const exportExcel = () => {
    // Exports are business records: timestamps pinned to America/Halifax regardless of
    // the exporting browser's timezone (matches on-screen rendering for NS users).
    const fmtX = (d) => d ? new Date(d).toLocaleString("en-CA",
      { timeZone: "America/Halifax", dateStyle: "medium", timeStyle: "short" }) : "—";
    const fmtDueX = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-CA",
      { dateStyle: "medium" }) : "—";
    const data = visible.map((m) => {
      const base = m.rowType === "sub" ? {
      "Submitted at": fmtX(m.r.created_at),
      "Completed at": m.r.completed_at ? fmtX(m.r.completed_at) : "—",
      "Form / Document": m.r.template_title,
      "Type": m.r.template_type,
      "Location": m.r.location_name,
      "Employee": m.r.worker_name,
      "Status": assignBySubmission[m.r.id]?.status === "awaiting_signatures" ? "Awaiting signatures" : statusMeta(displayStatus(m.r)).label,
      "Source": m.r.source === "contractor" ? "Contractor (QR portal)" : (assignBySubmission[m.r.id]?.kind === "form" ? "Employee (scheduled)" : "Employee"),
      "Required signers": assignBySubmission[m.r.id] ? signersText(assignBySubmission[m.r.id]) : "",
      "Contractors": (m.r.contractor_names || []).join("; "),
      "Flagged": m.r.flagged ? "Yes" : "No",
      "Flag reasons": (m.r.flag_reasons || []).join("; "),
      "Revision": m.r.revision || 1,
      "ID": m.r.id,
    } : m.rowType === "ack" ? {
      "Submitted at": fmtX(m.a.created_at),
      "Completed at": m.a.completed_at ? fmtX(m.a.completed_at) : "—",
      "Form / Document": m.a.title,
      "Type": "Document acknowledgement",
      "Location": m.a.location_name,
      "Employee": m.a.worker_name,
      "Status": ACK_META[ackStatus(m.a)].label,
      "Source": "Employee",
      "Required signers": signersText(m.a),
      "Contractors": "",
      "Flagged": "No",
      "Flag reasons": "",
      "Revision": m.a.resource_version || 1,
      "ID": m.a.id,
    } : {
      "Submitted at": fmtX(m.a.created_at),
      "Completed at": `Due ${fmtDueX(m.a.due_date)}`,
      "Form / Document": m.a.title,
      "Type": "Scheduled form (assigned)",
      "Location": m.a.location_name,
      "Employee": m.a.worker_name,
      "Status": ACK_META[ackStatus(m.a)].label,
      "Source": "Employee (scheduled)",
      "Required signers": signersText(m.a),
      "Contractors": "",
      "Flagged": "No",
      "Flag reasons": "",
      "Revision": 1,
      "ID": m.a.id,
    };
      // Platform export carries the Company column so a cross-company
      // spreadsheet can never be mistaken for one client's data.
      return isSuperAdmin
        ? { "Company": companyName[m.r?.company_id || m.a?.company_id] || "—", ...base }
        : base;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [{ wch: 20 }, { wch: 20 }, { wch: 28 }, { wch: 22 }, { wch: 22 }, { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 44 }, { wch: 26 }, { wch: 8 }, { wch: 34 }, { wch: 8 }, { wch: 38 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Submissions");
    XLSX.writeFile(wb, `hakina-submissions-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Box data-testid="submissions-page">
      <Flex justify="space-between" align="flex-start" gap="4" flexWrap="wrap" mb="5">
        <Stack gap="1">
          <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Submissions</Heading>
          <Text color={colors.textSecondary} fontSize={{ base: "sm", md: "md" }}>
            Review, sign off and export your company's field submissions and document acknowledgements.
          </Text>
        </Stack>
        <HStack gap="2" flexWrap="wrap">
          {!isSuperAdmin && <ScheduleFormButton />}
          <Button onClick={exportExcel} disabled={visible.length === 0} size="sm" variant="outline"
            bg="transparent" borderColor={colors.border} color={colors.textPrimary}
            _hover={{ bg: colors.cream }} data-testid="export-excel-btn">
            <Download size={16} /> Export Excel ({visible.length})
          </Button>
        </HStack>
      </Flex>

      {isSuperAdmin && (
        <Flex mb="4" px="4" py="3" borderRadius="10px" bg={colors.tealTint} align="center" gap="3" flexWrap="wrap"
          data-testid="platform-view-banner">
          <Text fontSize="sm" fontWeight="700" color={colors.tealHover}>Platform view — all companies</Text>
          <select value={companyF} onChange={(e) => setCompanyF(e.target.value)} data-testid="platform-company-filter"
            style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 13, background: "white" }}>
            <option value="">All companies</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Flex>
      )}

      <SubmissionFilters f={f} set={set} hasFilters={hasFilters}
        onClear={() => setF({ type: "", location: "", worker: "", status: "", source: "", contractor: "", from: "", to: "" })}
        types={types} locations={locations} workers={workers} contractorNames={contractorNames} />

      {loading ? (
        <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>
      ) : visible.length === 0 ? (
        <Flex align="center" justify="center" minH="180px" borderRadius="14px" border="1px dashed"
          borderColor={colors.border} bg={colors.white} data-testid="submissions-empty">
          <Stack align="center" gap="2">
            <Icon as={Inbox} boxSize="7" color={colors.textSecondary} />
            <Text color={colors.textSecondary} fontSize="sm">
              {hasFilters ? "Nothing matches these filters."
                : !navigator.onLine ? "You're offline — submissions will appear once this device has loaded them online."
                : "No submissions yet."}
            </Text>
          </Stack>
        </Flex>
      ) : (
        <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" overflow="hidden" data-testid="submissions-table">
          {/* Header row (desktop) */}
          <Flex display={{ base: "none", md: "flex" }} px="5" py="3" bg={colors.cream} gap="3"
            fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em" color={colors.textSecondary}>
            <Box flex="2.2">Form / Document</Box>
            {isSuperAdmin && <Box flex="1.2">Company</Box>}
            <Box flex="1.4">Location</Box>
            <Box flex="1.3">Employee</Box>
            <Box flex="1.4">Submitted / Signed</Box>
            <Box w="150px">Status</Box>
          </Flex>
          {visible.map((m) =>
            m.rowType === "sub" ? (
              <SubmissionRow key={m.r.id} r={m.r} fa={assignBySubmission[m.r.id]}
                company={isSuperAdmin ? (companyName[m.r.company_id] || "—") : undefined}
                onOpen={() => navigate(`/submissions/${m.r.id}`)} />
            ) : m.rowType === "ack" ? (
              <AckRow key={m.a.id} a={m.a} company={isSuperAdmin ? (companyName[m.a.company_id] || "—") : undefined}
                onOpen={() => navigate(`/acknowledgements/${m.a.id}`)} />
            ) : (
              <AssignmentRow key={m.a.id} a={m.a} company={isSuperAdmin ? (companyName[m.a.company_id] || "—") : undefined} />
            ))}
        </Box>
      )}
    </Box>
  );
}
